import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { Skeleton } from '@/design-system'
import { PrivacyType } from '@/utils/constants'
import { ShareSheet } from '@/features/album/components/ShareSheet'
import { useGalleryStudioData } from '../hooks/useGalleryStudioData'
import { useStudioHistory } from '../hooks/useStudioHistory'
import { StudioTopBar } from '../components/StudioTopBar'
import { StudioToolRail } from '../components/StudioToolRail'
import { StudioCanvasStage } from '../components/StudioCanvasStage'
import { StudioPropertyPanel } from '../components/StudioPropertyPanel'
import { StudioFilmstrip } from '../components/StudioFilmstrip'
import {
  DeleteGalleryModal,
  DuplicateGalleryModal,
  RenameGalleryModal,
  SaveToAlbumModal,
} from '../components/modals'
import { StudioCategory } from '../types'

export function GalleryStudioScreen() {
  const { galleryId = '' } = useParams<{ galleryId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const studio = useGalleryStudioData(galleryId)
  const { undo, redo } = useStudioHistory()

  const [renameOpen, setRenameOpen] = useState(false)
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)

  // Album → studio handoff: confirm seeded prints once after open
  useEffect(() => {
    if (!galleryId) return
    if (searchParams.get('fromAlbum') !== '1') return
    try {
      const raw = sessionStorage.getItem(`ew-gallery-seeded:${galleryId}`)
      if (raw) {
        sessionStorage.removeItem(`ew-gallery-seeded:${galleryId}`)
        const meta = JSON.parse(raw) as { printCount?: number }
        const n = meta.printCount || 0
        if (n > 0) {
          toast.success(
            n === 1
              ? 'Your album photo is on the wall — fine-tune anytime'
              : `${n} album photos are on the walls — fine-tune anytime`,
            { duration: 4500 },
          )
        }
      }
    } catch {
      /* ignore */
    }
    const next = new URLSearchParams(searchParams)
    next.delete('fromAlbum')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryId])

  if (studio.loading) {
    return (
      <Box display='flex' alignItems='center' justifyContent='center' height='100vh'>
        <Skeleton rounded='md' width={280} height={40} />
      </Box>
    )
  }

  if (studio.isError || !studio.topic) {
    return (
      <Box display='flex' alignItems='center' justifyContent='center' height='100vh'>
        <Box textAlign='center'>
          <Box sx={{ fontSize: 15, fontWeight: 600, color: 'var(--ds-ink)', mb: 1 }}>
            Gallery not found
          </Box>
          <Box sx={{ fontSize: 13, color: 'var(--ds-ink-muted)' }}>
            It may have been deleted, or you don’t have access.
          </Box>
        </Box>
      </Box>
    )
  }

  const activeCategory = studio.currentTab as StudioCategory

  return (
    <Box display='flex' flexDirection='column' height='100vh' width='100%' overflow='hidden'>
      <StudioTopBar
        title={studio.topic.topicName || 'Untitled gallery'}
        isPrivate={studio.topic.visibility === PrivacyType.PRIVATE.toString()}
        isOwner={studio.isOwner}
        changesCount={studio.changesCount}
        saving={studio.saving}
        onBack={studio.goBack}
        onUndo={undo}
        onRedo={redo}
        onSave={studio.saveNow}
        onTogglePrivacy={studio.togglePrivacy}
        onShare={() => setShareOpen(true)}
        onScreenshot={studio.onClickTakeScreenshot}
        onRename={() => setRenameOpen(true)}
        onDuplicate={() => setDuplicateOpen(true)}
        onDelete={() => setDeleteOpen(true)}
      />

      <Box display='flex' flex={1} minHeight={0}>
        <StudioToolRail active={activeCategory} onSelect={studio.selectCategory as (c: StudioCategory) => void} />

        <StudioCanvasStage
          canvaRef={studio.canvaRef}
          assets={studio.assets}
          selection={studio.selectedAssets}
          selectedTab={studio.currentTab}
          patternData={studio.patternData}
          isEditMode={studio.isEditMode}
          onEdit={studio.handlePatternEdit}
          onPatternRestore={studio.handlePatternRestore}
          onScreenshotReady={studio.onScreenshotReady}
          onZoomIn={studio.zoomIn}
          onZoomOut={studio.zoomOut}
          onRecenter={studio.recenterCamera}
        />

        {studio.isEditMode && (
          <StudioPropertyPanel
            category={activeCategory}
            topic={studio.topic}
            selectedAssets={studio.selectedAssets}
            onSelect={studio.setSelection as (c: StudioCategory, v: any) => void}
            onMattingWidthChange={studio.setMattingWidth}
          />
        )}
      </Box>

      <StudioFilmstrip
        cameraPosition={studio.cameraHorizontalPosition}
        onCameraPositionChange={studio.setCameraPosition}
        isEditMode={studio.isEditMode}
        onTogglePreview={() => studio.setIsEditMode((v) => !v)}
      />

      <RenameGalleryModal
        open={renameOpen}
        initialName={studio.topic.topicName || ''}
        onClose={() => setRenameOpen(false)}
        onSubmit={studio.rename}
      />
      <DuplicateGalleryModal
        open={duplicateOpen}
        galleryName={studio.topic.topicName || 'this gallery'}
        onClose={() => setDuplicateOpen(false)}
        onConfirm={studio.duplicateGallery}
      />
      <DeleteGalleryModal
        open={deleteOpen}
        galleryName={studio.topic.topicName || 'this gallery'}
        onClose={() => setDeleteOpen(false)}
        onConfirm={studio.removeGallery}
      />
      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shareUrl={studio.shareLink}
        title='Share gallery'
      />
      <SaveToAlbumModal
        open={studio.screenshotModalOpen}
        file={studio.screenshotFile}
        onClose={studio.closeScreenshotModal}
      />
    </Box>
  )
}
